<template>
  <NForm ref="formRef" class="flex flex-col h-full" :model="formValues">
    <NFormItem :label="t('canvasUI.canvasBuilder.multimediaSettings.chooseMediaType')">
      <NSelect
        v-model:value="formValues.type"
        :disabled="isUploading"
        :options="[
          { label: t('canvasUI.canvasBuilder.multimediaSettings.image'), value: 'image' },
          { label: t('canvasUI.canvasBuilder.multimediaSettings.video'), value: 'video' },
        ]"
        @update:value="resetUpload"
      />
    </NFormItem>
    <NFormItem :label="t('canvasUI.canvasBuilder.multimediaSettings.externalOrAsset')">
      <NSelect
        v-model:value="formValues.asset_type"
        :disabled="isUploading"
        :options="[
          { label: t('canvasUI.canvasBuilder.multimediaSettings.uploadAsset'), value: 'pitcher_asset' },
          { label: t('canvasUI.canvasBuilder.multimediaSettings.externalLinkOrTemplate'), value: 'external' },
        ]"
        @update:value="resetUpload"
      />
    </NFormItem>
    <NFormItem
      v-if="formValues.asset_type === 'external'"
      :label="t('canvasUI.canvasBuilder.multimediaSettings.addMediaLink')"
      path="external_url"
      :rule="{
        trigger: ['input', 'blur'],
      }"
    >
      <NInput v-model:value="formValues.external_url" clearable placeholder="" />
    </NFormItem>
    <NFormItem
      v-if="formValues.asset_type === 'pitcher_asset'"
      path="to_upload"
      :rule="{
        validator: fileValidator,
        trigger: ['input', 'blur'],
      }"
    >
      <NUpload
        v-model:file-list="formValues.to_upload"
        :default-upload="false"
        :disabled="isUploading"
        :max="1"
        :multiple="false"
      >
        <NUploadDragger>
          <div>
            <CIcon icon="upload" size="26px" />
          </div>
          <div class="mt-3">{{ t('canvasUI.canvasBuilder.multimediaSettings.uploadHint') }}</div>
        </NUploadDragger>
      </NUpload>
    </NFormItem>
    <CButton block class="mt-auto" :loading="isUploading" type="primary" @click="onSave">
      {{ t('canvasUI.common.done') }}
    </CButton>
  </NForm>
</template>

<script setup lang="ts">
import {
  NForm,
  NFormItem,
  NInput,
  NSelect,
  NUpload,
  NUploadDragger,
  type FormInst,
  type FormItemRule,
  type UploadFileInfo,
} from 'naive-ui'
import { ref, reactive, inject, computed, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import { type CanvasRetrieve } from '@openapi'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import { getFileExtension } from '@canvas-builder/util/file.util'
import useConnectUpload from '@lib/composables/useConnectUpload'
import type { MultimediaProps } from '@canvas-builder/types/canvas'
import { type PitcherInfo } from '@lib/types/app'
import { SUPPORTED_IMAGE_TYPES, SUPPORTED_VIDEO_TYPES } from '@lib/constants/uploads.const'

const props = defineProps<{
  id: string
  usedInSectionId?: CanvasRetrieve['id']
  data?: MultimediaProps
}>()
const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
  'pitcherInfo',
  computed(() => undefined),
)
const isUploading = ref(false)
const { uploadToConnect } = useConnectUpload()
const { t } = useI18n()
const { setComponentEditMode, updateNodeData } = useCanvas()
const formRef = ref<FormInst | null>(null)
const formValues = reactive({
  type: props.data?.type ?? 'image',
  external_url: props.data?.asset_type === 'external' ? props.data?.external_url : '',
  asset_type: props.data?.asset_type ?? 'pitcher_asset',
  file: props.data?.asset_type === 'pitcher_asset' ? props.data?.file : null,
  to_upload: [],
} as MultimediaProps & { to_upload: UploadFileInfo[] })

function fileValidator(_: FormItemRule, files: Array<UploadFileInfo>): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!files?.length) {
      return reject(new Error(t('canvasUI.canvasBuilder.multimediaSettings.validation.requiredFile')))
    }
    const file = files?.[0]?.file
    if (!file) {
      return reject(new Error(t('canvasUI.canvasBuilder.multimediaSettings.validation.invalidFile')))
    }
    const extension = getFileExtension(file.name)
    if (formValues.type === 'image' && !SUPPORTED_IMAGE_TYPES.includes(extension)) {
      return reject(
        new Error(t('canvasUI.canvasBuilder.multimediaSettings.validation.invalidImageExtension', { extension })),
      )
    }
    if (formValues.type === 'video' && !SUPPORTED_VIDEO_TYPES.includes(extension)) {
      return reject(
        new Error(t('canvasUI.canvasBuilder.multimediaSettings.validation.invalidVideoExtension', { extension })),
      )
    }
    return resolve()
  })
}

function resetUpload() {
  formValues.to_upload = []
}

async function onSave() {
  formRef.value
    ?.validate(async (errors) => {
      if (!errors) {
        try {
          if (formValues.asset_type === 'pitcher_asset') {
            const file = formValues.to_upload?.[0]?.file
            if (!file) throw new Error('No file to upload')
            else if (!pitcherInfo.value) throw new Error('Pitcher info not available')

            const formData = new FormData()
            formData.append('content', file)
            formData.append('name', file.name)
            formData.append('instance_id', pitcherInfo.value.instanceId)
            formData.append('is_asset', 'true')
            formData.append('access_token', pitcherInfo.value.accessToken)
            isUploading.value = true
            const data = await uploadToConnect(formData)
            isUploading.value = false
            const payload = {
              type: formValues.type,
              asset_type: 'external',
              external_url: data.url,
            } satisfies MultimediaProps
            updateNodeData(payload, props.id, props.usedInSectionId)
            setComponentEditMode(false)
          } else {
            const payload = {
              type: formValues.type,
              asset_type: 'external',
              external_url: formValues.external_url,
            } satisfies MultimediaProps
            updateNodeData(payload, props.id, props.usedInSectionId)
            setComponentEditMode(false)
          }
        } catch (e) {
          console.error(`Error while saving multimedia component: ${e}`)
          isUploading.value = false
        }
      }
    })
    .catch(() => {})
}

defineExpose({ isBusy: isUploading })
</script>
